/**
 * Sculpture Edition Relation Mappers
 *
 * Maps SculptureEditionIn* relations to ArtworkIn* format for compatibility with
 * compute field functions. Mirrors tirage-relation-mappers.ts.
 */

import {
  ArtworkInAuction,
  ArtworkInBibliography,
  ArtworkInExhibition,
  SculptureEditionInAuction,
  SculptureEditionInBibliography,
  SculptureEditionInExhibition,
} from "./types";

export function mapSculptureInExhibitionToArtwork(
  exhibition: SculptureEditionInExhibition,
): Pick<
  ArtworkInExhibition,
  | "exhibition"
  | "oeuvresDansExpositionsDescriptionNumeroCatalogue"
  | "oeuvresDansExpositionsNotes"
> {
  return {
    exhibition: exhibition.exhibition,
    oeuvresDansExpositionsDescriptionNumeroCatalogue:
      exhibition.sculptureEditionDansExpositionsNumeroCatalogue,
    oeuvresDansExpositionsNotes: exhibition.sculptureEditionDansExpositionsNotes || "",
  };
}

export function mapSculptureInExhibitionsToArtwork(
  exhibitions: SculptureEditionInExhibition[] | undefined | null,
): Pick<
  ArtworkInExhibition,
  | "exhibition"
  | "oeuvresDansExpositionsDescriptionNumeroCatalogue"
  | "oeuvresDansExpositionsNotes"
>[] | undefined {
  if (!exhibitions || exhibitions.length === 0) {
    return undefined;
  }
  return exhibitions.map(mapSculptureInExhibitionToArtwork);
}

export function mapSculptureInAuctionToArtwork(
  auction: SculptureEditionInAuction,
): Pick<
  ArtworkInAuction,
  | "auction"
  | "oeuvresDansVentesDescriptionNumeroLot"
  | "oeuvresDansVentesDescriptionReproduitPage"
  | "oeuvresDansVentesDescriptionEstimationHaute"
  | "oeuvresDansVentesDescriptionEstimationBasse"
  | "oeuvresDansVentesDescriptionVendu"
  | "oeuvresDansVentesDescriptionResultatNonListe"
  | "oeuvresDansVentesDescriptionNonVenu"
  | "oeuvresDansVentesDescriptionNonVendu"
  | "oeuvresDansVentesDescriptionNonReproduit"
  | "oeuvresDansVentesDescriptionRetire"
  | "oeuvresDansVentesDescriptionMiseAPrix"
  | "oeuvresDansVentesDescriptionPrixMarteau"
  | "oeuvresDansVentesDescriptionPrixAvecFrais"
  | "oeuvresDansVentesDescriptionNotes"
> {
  return {
    auction: auction.auction,
    oeuvresDansVentesDescriptionNumeroLot: auction.sculptureEditionDansVentesNumeroLot,
    oeuvresDansVentesDescriptionReproduitPage: auction.sculptureEditionDansVentesReproduitPage,
    oeuvresDansVentesDescriptionEstimationHaute:
      auction.sculptureEditionDansVentesEstimationHaute,
    oeuvresDansVentesDescriptionEstimationBasse:
      auction.sculptureEditionDansVentesEstimationBasse,
    oeuvresDansVentesDescriptionVendu: auction.sculptureEditionDansVentesVendu,
    oeuvresDansVentesDescriptionResultatNonListe:
      auction.sculptureEditionDansVentesResultatNonListe,
    oeuvresDansVentesDescriptionNonVenu: auction.sculptureEditionDansVentesNonVenu,
    oeuvresDansVentesDescriptionNonVendu: auction.sculptureEditionDansVentesNonVendu,
    oeuvresDansVentesDescriptionNonReproduit: auction.sculptureEditionDansVentesNonReproduit,
    oeuvresDansVentesDescriptionRetire: auction.sculptureEditionDansVentesRetire,
    oeuvresDansVentesDescriptionMiseAPrix: auction.sculptureEditionDansVentesMiseAPrix,
    oeuvresDansVentesDescriptionPrixMarteau: auction.sculptureEditionDansVentesPrixMarteau,
    oeuvresDansVentesDescriptionPrixAvecFrais: auction.sculptureEditionDansVentesPrixAvecFrais,
    oeuvresDansVentesDescriptionNotes: auction.sculptureEditionDansVentesNotes,
  };
}

export function mapSculptureInAuctionsToArtwork(
  auctions: SculptureEditionInAuction[] | undefined | null,
): Pick<
  ArtworkInAuction,
  | "auction"
  | "oeuvresDansVentesDescriptionNumeroLot"
  | "oeuvresDansVentesDescriptionReproduitPage"
  | "oeuvresDansVentesDescriptionEstimationHaute"
  | "oeuvresDansVentesDescriptionEstimationBasse"
  | "oeuvresDansVentesDescriptionVendu"
  | "oeuvresDansVentesDescriptionResultatNonListe"
  | "oeuvresDansVentesDescriptionNonVenu"
  | "oeuvresDansVentesDescriptionNonVendu"
  | "oeuvresDansVentesDescriptionNonReproduit"
  | "oeuvresDansVentesDescriptionRetire"
  | "oeuvresDansVentesDescriptionMiseAPrix"
  | "oeuvresDansVentesDescriptionPrixMarteau"
  | "oeuvresDansVentesDescriptionPrixAvecFrais"
  | "oeuvresDansVentesDescriptionNotes"
>[] | undefined {
  if (!auctions || auctions.length === 0) {
    return undefined;
  }
  return auctions.map(mapSculptureInAuctionToArtwork);
}

export function mapSculptureInBibliographyToArtwork(
  bibliography: SculptureEditionInBibliography,
): Pick<
  ArtworkInBibliography,
  | "bibliography"
  | "oeuvresDansBibliographieDescriptionReproduitPage"
  | "oeuvresDansBibliographieDescriptionCitePage"
  | "oeuvresDansBibliographieDescriptionPhotographe"
  | "oeuvresDansBibliographieDescriptionReproductionCouleur"
  | "oeuvresDansBibliographieDescriptionNonPagine"
  | "oeuvresDansBibliographieDescriptionReproduitPlanche"
  | "oeuvresDansBibliographieDescriptionPlancheNonNumerotee"
  | "oeuvresDansBibliographieDescriptionListePage"
  | "oeuvresDansBibliographieDescriptionSousNumero"
  | "oeuvresDansBibliographieDescriptionReproductionCouverture"
  | "oeuvresDansBibliographieDescriptionTexteLibre"
  | "oeuvresDansBibliographieNotes"
> {
  return {
    bibliography: bibliography.bibliography,
    oeuvresDansBibliographieDescriptionReproduitPage:
      bibliography.sculptureEditionDansBibliographieReproduitPage,
    oeuvresDansBibliographieDescriptionReproduitPlanche:
      bibliography.sculptureEditionDansBibliographieReproduitPlanche,
    oeuvresDansBibliographieDescriptionPlancheNonNumerotee:
      bibliography.sculptureEditionDansBibliographiePlancheNonNumerotee,
    oeuvresDansBibliographieDescriptionCitePage:
      bibliography.sculptureEditionDansBibliographieCitePage,
    oeuvresDansBibliographieDescriptionListePage:
      bibliography.sculptureEditionDansBibliographieListePage,
    oeuvresDansBibliographieDescriptionSousNumero:
      bibliography.sculptureEditionDansBibliographieSousNumero,
    oeuvresDansBibliographieDescriptionPhotographe:
      bibliography.sculptureEditionDansBibliographiePhotographe,
    oeuvresDansBibliographieDescriptionReproductionCouleur:
      bibliography.sculptureEditionDansBibliographieReproductionCouleur,
    oeuvresDansBibliographieDescriptionNonPagine:
      bibliography.sculptureEditionDansBibliographieNonPagine,
    oeuvresDansBibliographieDescriptionReproductionCouverture:
      bibliography.sculptureEditionDansBibliographieReproductionCouverture,
    oeuvresDansBibliographieDescriptionTexteLibre:
      bibliography.sculptureEditionDansBibliographieTexteLibre,
    oeuvresDansBibliographieNotes: bibliography.sculptureEditionDansBibliographieNotes,
  };
}

export function mapSculptureInBibliographiesToArtwork(
  bibliographies: SculptureEditionInBibliography[] | undefined | null,
): Pick<
  ArtworkInBibliography,
  | "bibliography"
  | "oeuvresDansBibliographieDescriptionReproduitPage"
  | "oeuvresDansBibliographieDescriptionCitePage"
  | "oeuvresDansBibliographieDescriptionPhotographe"
  | "oeuvresDansBibliographieDescriptionReproductionCouleur"
  | "oeuvresDansBibliographieDescriptionNonPagine"
  | "oeuvresDansBibliographieDescriptionReproduitPlanche"
  | "oeuvresDansBibliographieDescriptionPlancheNonNumerotee"
  | "oeuvresDansBibliographieDescriptionListePage"
  | "oeuvresDansBibliographieDescriptionSousNumero"
  | "oeuvresDansBibliographieDescriptionReproductionCouverture"
  | "oeuvresDansBibliographieDescriptionTexteLibre"
  | "oeuvresDansBibliographieNotes"
>[] | undefined {
  if (!bibliographies || bibliographies.length === 0) {
    return undefined;
  }
  return bibliographies.map(mapSculptureInBibliographyToArtwork);
}
